
import {
  getCookie,
  removeCookie,
  setCriticalCookie,
} from "@/lib/auth/cookies";
import {
  logout as logoutApi,
  login as loginApi,
  requestUserRegistration,
  requestPasswordReset as requestPasswordResetApi,
  resendOtpRegisteredUser as resendOtpRegisteredUserApi,
  requestEmailUpdate as requestEmailUpdateApi,
  resetPasswordByPassword as resetPasswordByPasswordApi,
  updateEmail as updateEmailApi,
  activateAccount as activateAccountApi,
  resendActivationEmail as resendActivationEmailApi,
  requestRegistrationLink as requestRegistrationLinkApi,
  resendRegistrationLink as resendRegistrationLinkApi,
  validateRegistrationToken as validateRegistrationTokenApi,
  completeRegistration as completeRegistrationApi,
  validateResetToken as validateResetTokenApi,
  resetPasswordByToken as resetPasswordByTokenApi,
  verifyMfa,
  sendMfaEmailCode,
  setupMfa,
  confirmMfaSetup,
  disableMfa,
  regenerateRecoveryCodes,
} from "@/lib/api/clients/AuthenticationClient";
import type { RegistrationLinkInfo } from "@/lib/api/clients/AuthenticationClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import {
  isValidLanguageCode,
  validateMongoObjectId,
} from "@/lib/utils/input-validator";
import {
  isValidPassword,
  isValidEmail,
  isTextValid,
  normalizeEmail,
} from "@/lib/utils/input-validator";
import { getCompanyTaxIdValidationError } from "@/lib/utils/tax-id-validator";
import { FALLBACK_LANGUAGE } from "@/lib/utils/default-returner";
import { TokenResponse } from "@/types/responses/UserResponses";

export async function resetPasswordByPassword(
  oldPassword: string,
  newPassword: string,
): Promise<TokenResponse | ServerError> {
  try {
    // Retrieve access token from cookies

    // Validate old password and new password formats
    const validOldPassword = isValidPassword(oldPassword);
    const validNewPassword = isValidPassword(newPassword);
    if (!validOldPassword || !validNewPassword)
      throw new Error("validationError");

    // Call the API to reset password using old password.
    // Backend issues fresh accessToken + refreshToken HttpOnly cookies
    // via Set-Cookie on this response — browser stores them automatically.
    const response = await resetPasswordByPasswordApi(
      oldPassword,
      newPassword,
    );

    return response;
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function requestEmailUpdate(
  newEmail: string,
  password: string,
): Promise<void | ServerError> {
  try {
    newEmail = normalizeEmail(newEmail);
    const validEmail = isValidEmail(newEmail);
    const validPassword = isValidPassword(password);
    if (!validEmail || !validPassword) throw new Error("validationError");

    // Call the API to request email update
    await requestEmailUpdateApi(newEmail, password);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateEmail(
  otpCode: string,
): Promise<void | ServerError> {
  try {
    // Retrieve stored data from cookies

    await updateEmailApi(otpCode);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Handles user logout process. Backend's /logout endpoint clears the
 * accessToken + refreshToken HttpOnly cookies via Set-Cookie Max-Age=0
 * — no client-side cookie removal needed.
 */
export async function logout(): Promise<void> {
  try {
    await logoutApi();
  } catch {
    // Error handled silently
  }
}

/**
 * Authenticates user with email and password
 * Validates input data, calls login API, and sets authentication cookies
 * @param email - User email address
 * @param password - User password
 * @throws Error if validation fails or login is unsuccessful
 */
export type LoginResult =
  | {
      mfaRequired: true;
      mfaSetupRequired: boolean;
      mfaExpiresAt: number;
    }
  | {
      mfaRequired?: false;
      companyId: string;
      userId?: string;
    };

export async function login(
  email: string,
  password: string,
): Promise<LoginResult | ServerError> {
  try {
    // Validate email and password format before sending to API
    email = normalizeEmail(email);
    const validPassword = isValidPassword(password);
    const validEmail = isValidEmail(email);

    if (!validPassword || !validEmail) throw new Error("invalidCredentials");

    // Attempt to log in with provided credentials
    const response = await loginApi(email, password);

    // Check if MFA is required
    if (response.mfaRequired === "true") {
      // Backend set the MFA-pending accessToken cookie via Set-Cookie.
      const mfaSetupRequired = response.mfaSetupRequired === "true";
      const mfaExpiresAt = response.mfaExpiresAt ?? Date.now() + 5 * 60 * 1000;
      if (mfaSetupRequired) {
        await setCriticalCookie("mfaSetupRequired", "true", 300);
      }
      return { mfaRequired: true, mfaSetupRequired, mfaExpiresAt };
    }

    // No MFA — backend set HttpOnly auth cookies via Set-Cookie.
    // companyId/userId are returned for `useAuth().markAuthenticated` to
    // persist in tokenStore and flip AuthContext state.
    if (!response.companyId) throw new Error("unknown");
    return { companyId: response.companyId, userId: response.userId };
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Initiates the user registration process (link-based activation)
 * Validates input data, sends ALL data to backend, sets minimal cookies for check-email page
 */
export async function initiateRegistration(
  firstName: string,
  lastName: string,
  password: string,
  email: string,
  languageCode: string,
  inviteCode?: string,
  country?: string,
  customerType?: string,
  taxId?: string,
  acceptedDocuments?: Record<string, string>,
): Promise<void | ServerError> {
  try {
    // Validate all user input fields
    email = normalizeEmail(email);
    const validFirstName = isTextValid(firstName);
    const validLastName = isTextValid(lastName);
    const validPassword = isValidPassword(password);
    const validEmail = isValidEmail(email);
    const validLanguageCode = isValidLanguageCode(languageCode);
    const validInviteCode = inviteCode
      ? validateMongoObjectId(inviteCode) === ""
      : true;

    // If any validation fails, throw a validation error
    if (
      !validFirstName ||
      !validLastName ||
      !validPassword ||
      !validEmail ||
      !validLanguageCode ||
      !validInviteCode
    )
      throw new Error("validationError");

    // Non-invited users must provide customerType and country. The tax ID is
    // optional (clubs, Kleinunternehmer, freelancers have none); a given one
    // is still checked for plausibility.
    if (!inviteCode) {
      if (!customerType || !country) throw new Error("validationError");
      // B2B-only (Kanzlei 7.1): no new INDIVIDUAL account. The backend
      // refuses it with the same code; failing here saves the round trip.
      if (customerType === "INDIVIDUAL") throw new Error("customerTypeIndividualNotOffered");
      if (customerType !== "COMPANY") {
        throw new Error("customerTypeRequired");
      }
      if (customerType === "COMPANY") {
        const taxIdValidationError = getCompanyTaxIdValidationError({
          customerType,
          country,
          taxId,
        });
        if (taxIdValidationError) throw new Error(taxIdValidationError);
      }
    }

    // Send all data to backend — password is never stored in cookies
    await requestUserRegistration(
      firstName,
      lastName,
      email,
      password,
      languageCode,
      inviteCode,
      country,
      customerType,
      taxId,
      acceptedDocuments,
    );

    // Only set minimal cookies for the check-email page (30 min TTL).
    // `register-invite` origin keeps invitees on the LEGACY activation flow
    // (full pending row → /auth/activate), distinct from the self-serve
    // email-first `register` origin so resend dispatches to the right endpoint.
    const checkEmailTtl = 60 * 30;
    await setCriticalCookie("email", email, checkEmailTtl);
    await setCriticalCookie("origin", "register-invite", checkEmailTtl);
    await setCriticalCookie("emailResend", `${Date.now() + 99 * 1000}`, checkEmailTtl);
    // Persist the invite code so the check-email "Back" can return to the
    // invite form (/auth/register?invite_code=…) instead of the self-serve one.
    if (inviteCode) await setCriticalCookie("inviteCode", inviteCode, checkEmailTtl);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Email-first step 1: requests a magic link with email only. No PII beyond
 * the email is collected until the link is clicked. Sets the same check-email
 * cookies the /auth/check-email page reads (email / origin / emailResend).
 */
export async function requestRegistrationLink(
  email: string,
  languageCode: string,
): Promise<void | ServerError> {
  try {
    email = normalizeEmail(email);
    const validEmail = isValidEmail(email);
    if (!validEmail) throw new Error("validationError");
    let lang = languageCode;
    if (!isValidLanguageCode(lang)) lang = FALLBACK_LANGUAGE;

    await requestRegistrationLinkApi(email, lang);

    const checkEmailTtl = 60 * 30;
    await setCriticalCookie("email", email, checkEmailTtl);
    await setCriticalCookie("origin", "register", checkEmailTtl);
    await setCriticalCookie("emailResend", `${Date.now() + 99 * 1000}`, checkEmailTtl);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Email-first step 2: read-only validation of the magic-link token so the
 * complete-signup page can prefill the email and force-match the locale.
 */
export async function validateRegistrationToken(
  token: string,
): Promise<RegistrationLinkInfo | ServerError> {
  try {
    if (!token || token.length < 20) throw new Error("validationError");
    return await validateRegistrationTokenApi(token);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Email-first step 3: submits the full form against the token. Backend
 * creates the account and sets HttpOnly auth cookies (auto-login) — the
 * caller flips AuthContext via markAuthenticated. Mirrors `login`'s response
 * parsing (MFA vs full). Clears the check-email cookies on success.
 */
export async function completeRegistration(
  token: string,
  firstName: string,
  lastName: string,
  password: string,
  country?: string,
  customerType?: string,
  taxId?: string,
  acceptedDocuments?: Record<string, string>,
): Promise<LoginResult | ServerError> {
  try {
    if (!token || token.length < 20) throw new Error("validationError");
    const validFirstName = isTextValid(firstName);
    const validLastName = isTextValid(lastName);
    const validPassword = isValidPassword(password);
    if (!validFirstName || !validLastName || !validPassword)
      throw new Error("validationError");

    // Self-serve (non-invite) needs customerType + country; the tax ID is
    // optional, a given one is still checked.
    // Invite completions omit these (the invited org is resolved server-side).
    if (customerType || country) {
      if (!customerType || !country) throw new Error("validationError");
      // B2B-only (Kanzlei 7.1) — see initiateRegistration.
      if (customerType === "INDIVIDUAL")
        throw new Error("customerTypeIndividualNotOffered");
      if (customerType !== "COMPANY")
        throw new Error("customerTypeRequired");
      if (customerType === "COMPANY") {
        const taxIdValidationError = getCompanyTaxIdValidationError({
          customerType,
          country,
          taxId,
        });
        if (taxIdValidationError) throw new Error(taxIdValidationError);
      }
    }

    const response = await completeRegistrationApi(
      token,
      firstName,
      lastName,
      password,
      country,
      customerType,
      taxId,
      acceptedDocuments,
    );

    // MFA branch (e.g. invite into a company with requireMfa).
    if (response.mfaRequired === "true") {
      const mfaSetupRequired = response.mfaSetupRequired === "true";
      const mfaExpiresAt = response.mfaExpiresAt ?? Date.now() + 5 * 60 * 1000;
      if (mfaSetupRequired) {
        await setCriticalCookie("mfaSetupRequired", "true", 300);
      }
      await removeCookie("email");
      await removeCookie("origin");
      await removeCookie("emailResend");
      return { mfaRequired: true, mfaSetupRequired, mfaExpiresAt };
    }

    if (!response.companyId) throw new Error("unknown");

    await removeCookie("email");
    await removeCookie("origin");
    await removeCookie("emailResend");

    return { companyId: response.companyId, userId: response.userId };
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Initiates password reset process for a user (link-based)
 * Validates email, requests password reset link, sets cookies for check-email page
 */
export async function requestPasswordReset(
  email: string,
): Promise<void | ServerError> {
  try {
    email = normalizeEmail(email);
    const validEmail = isValidEmail(email);
    if (!validEmail) throw new Error("validationError");

    await requestPasswordResetApi(email);

    // Set cookies for the check-email page (30 min TTL)
    const checkEmailTtl = 60 * 30;
    await setCriticalCookie("email", email, checkEmailTtl);
    await setCriticalCookie("origin", "reset-password", checkEmailTtl);
    await setCriticalCookie("emailResend", `${Date.now() + 99 * 1000}`, checkEmailTtl);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Activates a user account via email link token
 */
export async function activateAccount(
  token: string,
): Promise<void | ServerError> {
  try {
    if (!token || token.length < 20) throw new Error("validationError");
    await activateAccountApi(token);

    // Clean up leftover check-email cookies
    await removeCookie("email");
    await removeCookie("origin");
    await removeCookie("emailResend");
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Resends email for check-email page (registration activation or password reset link)
 * Checks cooldown cookie before calling the appropriate API
 */
export async function resendCheckEmail(
  origin: string,
  email: string,
  languageCode: string,
): Promise<void | ServerError> {
  try {
    // Check cooldown
    const emailResend = await getCookie("emailResend");
    if (emailResend && parseInt(emailResend) > Date.now())
      throw new Error("otpResend");

    // The email arrives from the check-email cookie, which a pre-normalize
    // deploy may have written mixed-case — normalize here too.
    email = normalizeEmail(email);
    const validEmail = isValidEmail(email);
    if (!validEmail) throw new Error("validationError");

    const validLanguageCode = isValidLanguageCode(languageCode);
    if (!validLanguageCode) languageCode = FALLBACK_LANGUAGE;

    if (origin === "register") {
      // Self-serve email-first flow: resend the thin magic link (not the
      // legacy activation email, whose template expects a FIRST_NAME the thin
      // pending row no longer carries).
      await resendRegistrationLinkApi(email, languageCode);
    } else if (origin === "register-invite") {
      // Invitees still ride the legacy activation flow (full pending row).
      await resendActivationEmailApi(email, languageCode);
    } else if (origin === "reset-password") {
      await requestPasswordResetApi(email);
    } else {
      throw new Error("validationError");
    }

    // Set 99s cooldown
    await setCriticalCookie("emailResend", `${Date.now() + 99 * 1000}`);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Cancels the check-email flow by removing related cookies
 */
export async function cancelCheckEmailFlow(): Promise<void> {
  await removeCookie("email");
  await removeCookie("origin");
  await removeCookie("emailResend");
  await removeCookie("inviteCode");
}

/**
 * Validates a password reset token (for page load, does not consume the token)
 */
export async function validateResetToken(
  token: string,
): Promise<{ email: string; expiresAt: number } | ServerError> {
  try {
    if (!token || token.length < 20) throw new Error("validationError");
    return await validateResetTokenApi(token);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Resends OTP for registered users (e.g., for email update or other verification)
 * Validates type and companyId, checks resend cooldown, and calls the API
 * @param type - Type of OTP resend (e.g., "email-update", "verification")
 * @param companyId - Company ID associated with the user
 * @throws Error if validation fails or resend cooldown is active
 */
export async function resendOtpRegisteredUser(
  type: string,
): Promise<void | ServerError> {
  try {
    // Retrieve stored data from cookies
    const otpResend = await getCookie("otpResend");
    const companyId = (await getCookie("companyId")) as string;

    // Validate type (should not be empty)
    const validType = isTextValid(type);

    // Validate companyId as MongoDB ObjectId
    const validCompanyId = validateMongoObjectId(companyId) === "";

    // Check if resend cooldown is still active
    if (otpResend && parseInt(otpResend) > Date.now())
      throw new Error("otpResend");

    // Validate all inputs
    if (!validType || !validCompanyId) throw new Error("validationError");

    let otpExpiration = null;
    // Resend OTP through the API
    otpExpiration = await resendOtpRegisteredUserApi(
      type,
      companyId,
    );
    otpExpiration = otpExpiration - 10 * 1000; // Subtract 10 seconds to account for network latency

    // Check if OTP is about to expire (within 10 seconds)
    if (otpExpiration < Date.now()) throw new Error("otpExpired");

    // Set resend cooldown (99 seconds)
    await setCriticalCookie("otpResend", `${Date.now() + 99 * 1000}`);

    // Store OTP expiration for the verification step
    await setCriticalCookie(
      "otpExpiration",
      otpExpiration.toString(),
      (otpExpiration - Date.now()) / 1000,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Completes password reset using a token from the email link
 */
export async function completeResetPassword(
  token: string,
  newPassword: string,
): Promise<void | ServerError> {
  try {
    if (!token || token.length < 20) throw new Error("validationError");
    const validPassword = isValidPassword(newPassword);
    if (!validPassword) throw new Error("validationError");

    await resetPasswordByTokenApi(token, newPassword);

    // Clear reset/check-email flow cookies so middleware doesn't bounce user back
    await removeCookie("email");
    await removeCookie("origin");
    await removeCookie("emailResend");
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function handleMfaVerify(
  code: string,
  type: string,
): Promise<{ companyId: string; userId?: string } | ServerError> {
  try {
    // Validate type
    if (!["totp", "recovery", "email"].includes(type))
      throw new Error("validationError");

    // Validate code format
    if (type === "totp" || type === "email") {
      if (!/^\d{6}$/.test(code)) throw new Error("validationError");
    } else if (type === "recovery") {
      if (!/^[a-zA-Z0-9]{8}$/.test(code)) throw new Error("validationError");
    }

    const response = await verifyMfa(code, type);

    // Tokens ride in HttpOnly cookies set by the backend; companyId is
    // returned for `useAuth().markAuthenticated` to persist + flip state.
    // Clear MFA-flow hints from localStorage (HttpOnly cookies cleared
    // by backend via Set-Cookie).
    await removeCookie("mfaSetupRequired");
    await removeCookie("mfaEmailResend");
    return { companyId: response.companyId, userId: response.userId };
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function handleMfaSetup(): Promise<{ secret: string; otpAuthUri: string } | ServerError> {
  try {
    // Auth ride: HttpOnly accessToken cookie auto-attaches. Whether
    // it carries an MFA_PENDING role (forced-setup flow) or a regular
    // role (settings flow) is decided server-side by the JWT in the
    // cookie. The legacy `accessToken` argument is ignored by the API
    // shim (axios sends the cookie via `withCredentials: true`).
    return await setupMfa();
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function handleMfaConfirmSetup(
  code: string,
): Promise<
  { recoveryCodes: string[]; companyId?: string; userId?: string } | ServerError
> {
  try {
    // Auth ride: same as handleMfaSetup — HttpOnly cookie carries the
    // JWT; the explicit token parameter is ignored by the shim.
    const response = await confirmMfaSetup(code);

    if (response.companyId) {
      // Forced-setup flow finishing: backend issued fresh full-role
      // cookies via Set-Cookie. companyId is returned for the form to
      // pass to `useAuth().markAuthenticated` once the user clicks Done.
      await removeCookie("mfaSetupRequired");
    }

    return {
      recoveryCodes: response.recoveryCodes,
      companyId: response.companyId,
      userId: response.userId,
    };
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function handleMfaDisable(
  password: string,
  code: string,
): Promise<void | ServerError> {
  try {
    await disableMfa(password, code);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function handleMfaRegenerateRecoveryCodes(
  password: string,
  code: string,
): Promise<{ recoveryCodes: string[] } | ServerError> {
  try {
    return await regenerateRecoveryCodes(password, code);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function handleMfaSendEmailCode(): Promise<void | ServerError> {
  try {
    // Check cooldown
    const mfaEmailResend = await getCookie("mfaEmailResend");
    if (mfaEmailResend && parseInt(mfaEmailResend) > Date.now())
      throw new Error("otpResend");

    // Auth ride: HttpOnly accessToken cookie carries the MFA_PENDING JWT.
    await sendMfaEmailCode();

    // Set 99s cooldown
    await setCriticalCookie(
      "mfaEmailResend",
      `${Date.now() + 99000}`,
      99,
    );
  } catch (error) {
    return handleErrorServer(error);
  }
}

/**
 * Cancels the MFA flow by removing MFA-related cookies.
 * Safe to call at any point before handleMfaConfirmSetup completes (steps 1-3).
 */
export async function cancelMfaFlow(): Promise<void> {
  // mfaAccessToken is HttpOnly; backend clears it on the next login or
  // on this navigation by issuing a fresh accessToken cookie. The legacy
  // localStorage flags below are still cockpit-owned UI state.
  await removeCookie("mfaSetupRequired");
  await removeCookie("mfaEmailResend");
}

/**
 * Cancels the verification flow by removing verification-related cookies.
 * Used by reset-email and delete-company OTP flows.
 */
export async function cancelVerificationFlow(): Promise<void> {
  await removeCookie("origin");
  await removeCookie("otpResend");
}
